import { batch } from "omi";
import includes from "lodash-es/includes";
import isEmpty from "lodash-es/isEmpty";

import { UserSlice } from "@/store/user";
import { NetworkSignal, networkSignal } from "@/utils/signals";
import { createStore } from "@/utils/store";
import { GoalRoadmap } from "@/api";
import { openLoginModal } from "@/wc/components";
import { Store } from "@/store";
import { initApi } from "@/utils";

import { CourseSearchResult } from "../../../views/explore/courseView";
import { toCourseResult } from "./transformers.ts";

type State = {
  goal: string;
  activeView: "goalSelect" | "loadingView" | "resultView";
  didCreate: boolean;
  isEditingGoal: boolean;
};

type NetworkState = {
  madeForYou: NetworkSignal<CourseSearchResult | undefined>;
};

type Actions = {
  fetchMadeForYou: () => void;
  setGoal: (goal: string) => void;

  getMadeForYou: () => CourseSearchResult | undefined;
  createMadeForYou: (intentionalFriction?: boolean) => void;
  setActiveView: (view: "goalSelect" | "loadingView" | "resultView") => void;
  showEditView: () => void;
  resetActiveView: () => void;
  updateSelected: (ids: string[], selected: boolean) => void;
};

type Slices = {
  userSlice: UserSlice;
};

function createMadeForYouStore() {
  const userSlice = Store.user;
  const api = initApi();

  return createStore<State, NetworkState, Actions, Slices>({
    initialState: {
      goal: "",
      activeView: "goalSelect",
      didCreate: false,
      isEditingGoal: false,
    },
    networkState: {
      madeForYou: networkSignal<CourseSearchResult | undefined>(),
    },
    slices: () => ({
      userSlice,
    }),
    actions: ({ store, networkState, slices }) => ({
      fetchMadeForYou: async () => {
        if (networkState.madeForYou.signal.data.value || !slices.userSlice.isLoggedIn.value) {
          return;
        }

        const results = await networkState.madeForYou.run(async () => {
          const res = await api.getGoal();
          const roadmap = res.suggestedRoadmap;

          if (!roadmap) {
            return undefined;
          }

          const projects = roadmap.results || [];
          const goal = res.goal?.goalDescription || "";
          store.goal.value = goal;

          return toCourseResult(goal, projects);
        });

        if (results) {
          store.activeView.value = "resultView";
        } else {
          store.activeView.value = "goalSelect";
        }
      },
      setGoal: (goal: string) => {
        store.goal.value = goal;
      },
      getMadeForYou: () => {
        return networkState.madeForYou.signal.data.value ? networkState.madeForYou.signal.data.value : undefined;
      },
      createMadeForYou: async (intentionalFriction = false) => {
        const goal = store.goal.value;
        if (isEmpty(goal)) {
          return;
        }

        if (!slices.userSlice.isLoggedIn.value) {
          openLoginModal();
          return;
        }

        store.isEditingGoal.value = false;
        store.activeView.value = "loadingView";

        const results = await networkState.madeForYou.run(async () => {
          const promises: Promise<GoalRoadmap | void>[] = [api.updateGoal({ goalUpdate: { goal } })];

          if (intentionalFriction) {
            const minimumWaitPromise = new Promise<void>(resolve => {
              setTimeout(() => resolve(), 6000);
            });
            promises.push(minimumWaitPromise);
          }

          const [res] = await Promise.all(promises);
          const roadmap = (res as GoalRoadmap).suggestedRoadmap;
          const projects = roadmap?.results || [];
          return toCourseResult(goal, projects);
        });

        if (results) {
          store.activeView.value = "resultView";
          store.didCreate.value = true;
        } else {
          store.activeView.value = "goalSelect";
        }
      },

      setActiveView: (view: "goalSelect" | "loadingView" | "resultView") => {
        store.activeView.value = view;
      },
      showEditView: () => {
        batch(() => {
          store.activeView.value = "goalSelect";
          store.isEditingGoal.value = true;
        });
      },
      updateSelected: (ids: string[], selected: boolean) => {
        const madeForYouResults = networkState.madeForYou.signal.data.value;
        if (!madeForYouResults) {
          return;
        }

        const entities = madeForYouResults.entities || [];

        const projects = entities?.filter(p => includes(ids, p.id));
        if (projects) {
          projects.forEach(p => {
            p.metadata?.forEach(m => {
              if (m.key === "selected") {
                m.value = selected ? "true" : "false";
              }
            });
          });
        }

        networkState.madeForYou.signal.data.value = { ...madeForYouResults, entities };
      },
      resetActiveView: () => {
        const hasGoalRoadmap = !!networkState.madeForYou.signal.data.value;

        if (hasGoalRoadmap) {
          store.activeView.value = "resultView";
        } else {
          store.activeView.value = "goalSelect";
        }
      },
    }),
  });
}

export const MadeForYouStore = createMadeForYouStore();
